import { Windows95Window } from "../Windows95Window";
import {
    Container,
    Graphics,
    NineSlicePlane,
    Rectangle,
    RenderTexture,
    Sprite,
    Text,
    TextStyle,
    Texture
} from "pixi.js";
import { GAME_FONT, GAME_FONT_BOLD } from "../../Constants/Constants";
import { tsthreeConfig } from "../../../config/tsthreeConfig";
import { formatTimestampAs12HrClock } from "../../../engine/HelperFunctions/formatTimestampAs12HrClock";
import { buttonify } from "../../../engine/HelperFunctions/buttonify";
import { IMailInstanceData } from "../../Types/IMailInstanceData";
import { MailState } from "../../Types/MailState";
import { MailCore } from "../../Logic/MailCore";
import { SpecialMailLogic } from "../../Logic/SpecialMailLogic";
import TaggedText from "pixi-tagged-text";
import { TextStyleSet } from "pixi-tagged-text/dist/types";
import { MailType } from "../../Types/MailType";
import { GameObject, HelperFunctions, IVector2 } from "../../../tsthree";
import { ScrollBar } from "../ScrollBar";
import { FileEditHelpBar } from "../FileEditHelpBar";
import { PlayerDataSingleton } from "../../Logic/PlayerDataSingleton";
import { GAME_DEBUG_MODE } from "../../../engine/Constants/Constants";
import { IWindowState } from "../../Types/IWindowState";
import { SAVE_KEYS } from "../../Constants/SaveKeys";
import { removeTagsFromString } from "../../../engine/HelperFunctions/removeTagsFromString";
import { TutorialStageComponent } from "../../Components/TutorialStageComponent";
import { createPatientPictureFromPatientName } from "../../Logic/createPatientPictureFromPatientName";
import { formatTimestampToHHMM } from "../../../engine/HelperFunctions/formatTimestampToHHMM";
import { Windows95 } from "../../States/Windows95";
import { uid } from "../../../engine/HelperFunctions/uid";

const DRAW_WHITESPACE_IN_MAIN_TEXT = false;

export class Win95MailWindow extends Windows95Window {

    private _mailDatasRef: IMailInstanceData[] = [];
    private prevLength: number;
    private mailsOffset: number = 0;

    private selectedMailBg: NineSlicePlane;
    private selectedMailContentsContainer: Container;
    private selectedMailRef: IMailInstanceData;

    private popoutText: Text;
    private popoutSprite: Sprite;

    private patientPortraitStuff: {
        currentTexture: RenderTexture;
        currentPatientName: string;
        container: Container;
        sprite: Sprite;
    } = {
        currentTexture: null,
        currentPatientName: "",
        container: null,
        sprite: null,
    };

    private zoomFactor: number = 1;
    private htmlTextStyle: TextStyleSet = null;

    private emailsPool: Array<{
        container: GameObject,
        update: (_email: IMailInstanceData) => void,
        setSelected: (_selected: boolean) => void,
    }> = [];

    private scrollBar: ScrollBar = null;

    private selectedMailEntryGraphic: Graphics;
    private selectedMailContents: {
        container: GameObject,
        contentContainer: Container,
        getContentsText: () => TaggedText,
        selectedMailBg: NineSlicePlane,
        subjectContentsText: Text;
        update: (_email?: IMailInstanceData) => void;
    };

    private static _mailTextCache: Record<string, TaggedText> = {};

    constructor(
        settings: {
            mailInstances: Array<IMailInstanceData>,
            onMinimise: () => void;
            onClose: () => void;
            openLink: (url: string, caseId?: string) => void;
            onOpenPopoutWindow: (_contentsStr: string, _subject: string) => void;
            windowStateRef: IWindowState<Win95MailWindow>,

            // unused?
            onUpdateMailState: () => void;
        }
    ) {
        super({
            title: "Mail Server",
            iconKey: "unread-icon",
            onMinimise: settings.onMinimise,
            onClose: settings.onClose,
            windowStateRef: settings.windowStateRef,
        });

        this.zoomFactor = PlayerDataSingleton.getMailboxZoom();
        this.mailDatasRef = settings.mailInstances;

        const mainMailContainer = new Container();
        const mainMailBg = new NineSlicePlane(
            ENGINE.getPIXIResource("inner-container-nineslice") as Texture
        );
        mainMailBg.width = tsthreeConfig.width - 2;
        mainMailBg.height = 183;
        mainMailContainer.addChild(mainMailBg);
        this.addChild(mainMailContainer);
        mainMailContainer.position.set(0, 42);

        // begin scrollbar
        const mailListScrollBar = this.scrollBar = new ScrollBar({
            height: mainMailBg.height - 23,
            onScroll: (_currIncrements) => {
                this.mailsOffset = _currIncrements;
                this.mailsOffset = Math.min(Math.max(
                    this.mailsOffset,
                    0
                ), Math.max(this.mailDatasRef.length - 10, 0));
                this.emailsPool.forEach((e, index) => {
                    e.update(this.mailDatasRef[index+this.mailsOffset]);
                });
                this.emailsPool.forEach((e) => e.setSelected(false));
            }
        });
        let pointerOver = false;
        mainMailContainer.interactive = true;
        mainMailContainer.on("pointerover", () => {
            pointerOver = true;
        });
        mainMailContainer.on("pointerout", () => {
            pointerOver = false;
        });
        document.addEventListener("wheel", (ev) => {
            if(
                !pointerOver
            ) return;
            mailListScrollBar.scrollWheel((ev.deltaY / 100));
        });
        // Win95MailWindow.createScrollBar({
        //     height: mainMailBg.height,
        //     onScroll: (_currIncrements) => {
        //         // handle updating mail list here
        //         this.mailsOffset = _currIncrements;
        //         this.mailsOffset = Math.min(Math.max(
        //             this.mailsOffset,
        //             0
        //         ), Math.max(this.mailDatasRef.length - 10, 0));
        //         this.emailsPool.forEach((e, index) => {
        //             e.update(this.mailDatasRef[index+this.mailsOffset]);
        //         });
        //         this.emailsPool.forEach((e) => e.setSelected(false));
        //     }
        // });
        mailListScrollBar.position.set(tsthreeConfig.width - 2, 18);
        mainMailContainer.addChild(mailListScrollBar);
        // end scrollbar

        const tabWidths = [
            Math.floor((tsthreeConfig.width - 2) * 0.2),
            Math.floor((tsthreeConfig.width - 2) * 0.6),
            Math.floor((tsthreeConfig.width - 2) * 0.2)
        ];
        const totalWidth = tabWidths.reduce((p, c) => p + c);
        const tabContainer = new Container();
        const fromTab = Win95MailWindow.createTab("From", tabWidths[0]);
        const subjectTab = Win95MailWindow.createTab("Subject", tabWidths[1]);
        subjectTab.position.set(fromTab.position.x + fromTab.width, 0);
        const receivedTab = Win95MailWindow.createTab("Received", tabWidths[2]);
        receivedTab.position.set(subjectTab.position.x + subjectTab.width, 0);
        tabContainer.addChild(fromTab);
        tabContainer.addChild(subjectTab);
        tabContainer.addChild(receivedTab);
        tabContainer.position.set(2, 2);
        mainMailContainer.addChild(tabContainer);

        const tabHeight = (ENGINE.getPIXIResource("tab-nineslice") as Texture).height;

        const selectedMailContainer = this.selectedMailContents = Win95MailWindow.createSelectedMailSection(
            this,
            tsthreeConfig.height - (
                0
            ) - (
                mainMailContainer.position.y
            ) - (
                mainMailContainer.height
            ) - 48,
            (_partialUrl: string) => {
                // const searchUrl = `${SEARCH_HOMEPAGE}/${_partialUrl}`;
                if(this.selectedMailRef.id.indexOf("CASE_") === 0) {
                    settings.openLink(_partialUrl, this.selectedMailRef.id.substring("CASE_".length));
                } else {
                    settings.openLink(_partialUrl);
                }
            }
        );
        this.patientPortraitStuff.container = selectedMailContainer.portraitContainer;
        this.patientPortraitStuff.sprite = selectedMailContainer.portraitSprite;
        this.selectedMailContentsContainer = selectedMailContainer.contentContainer;
        this.selectedMailBg = selectedMailContainer.selectedMailBg;
        selectedMailContainer.container.position.set(
            0,
            mainMailContainer.position.y + mainMailContainer.height
        );
        this.addChild(selectedMailContainer.container);

        const emailContainer = new Container();
        for(let i = 9; i >= 0; i--) {
            const mail = Win95MailWindow.createMailLine({
                tabWidths: tabWidths
            });
            this.emailsPool.push(mail);
        }
        this.emailsPool.forEach((e, i) => {
            buttonify(e.container, {
                disableButtonMode: true,
                onFire: (ev) => {
                    if(!this.mailDatasRef[i+this.mailsOffset])
                        return;
                    const mailData = this.mailDatasRef[i+this.mailsOffset];
                    this.selectedMailRef = mailData;
                    this.emailsPool.forEach((e) => e.setSelected(false));
                    e.setSelected(true);
                    if(mailData.state == MailState.UNREAD) {
                        MailCore.markEmailAsRead(mailData.id);
                        if(SpecialMailLogic[mailData.id]) {
                            SpecialMailLogic[mailData.id].onRead();
                        }
                        e.update(this.mailDatasRef[i+this.mailsOffset]);

                        // unused?
                        settings.onUpdateMailState();
                    }
                    selectedMailContainer.update(this.mailDatasRef[i+this.mailsOffset]);
                }
            });
            e.container.hitArea = new Rectangle(0,0,totalWidth - 16,16);
            e.container.position.set(
                0,
                tabHeight + 2 + (16 * i)
            );
            emailContainer.addChild(e.container);
        });

        // for(
        //     let i = emailDatas.length-1, x = 0;
        //     i >= (emailDatas.length - 10);
        //     i--, x++
        // ) {
        //     const emailData = emailDatas[i];
        //     emails[x].update(emailData);
        // }
        this.emailsPool.forEach((e, index) => {
            e.update(this.mailDatasRef[index]);
        });

        const mailListMask = new Graphics();
        mailListMask.beginFill(0xff0000, 1);
        mailListMask.drawRect(0, 0, tsthreeConfig.width, mainMailBg.height - 2);
        mailListMask.endFill();
        emailContainer.mask = mailListMask;
        emailContainer.addChild(mailListMask);

        mainMailContainer.addChild(emailContainer);
        mailListScrollBar.update(
            Math.max(this._mailDatasRef.length - 10, 0)
        );

        const popOutButton = this.popoutSprite = new Sprite(
            ENGINE.getPIXIResource("popout-icon") as Texture
        );
        popOutButton.width = 16;
        popOutButton.height = 16;
        popOutButton.anchor.set(0.5);
        popOutButton.position.set(tsthreeConfig.width - 15, 256);
        this.addChild(popOutButton);
        const popoutText = this.popoutText = new Text(
            "Popout email",
            new TextStyle({
                fill: 0x0000ff,
                fontSize: 11,
                align: "right",
                fontFamily: GAME_FONT,
            })
        );
        popoutText.anchor.set(1, 0);
        popoutText.position.set(popOutButton.position.x - 16, popOutButton.y - 6);
        this.addChild(popoutText);
        buttonify([
            popOutButton,
            popoutText
        ], {
            onFire: () => {
                if(!this.selectedMailContents.getContentsText().text) return;
                settings.onOpenPopoutWindow(
                    this.selectedMailContents.getContentsText().text,
                    this.selectedMailContents.subjectContentsText.text
                );
            }
        });
        popoutText.visible = false;
        popOutButton.visible = false;

        const tutorialComponent = new TutorialStageComponent({
            id: "DeleteEmailStage",
        });
        const fileEditViewText = new FileEditHelpBar([
            {
                name: "File",
                subcontents: [
                    {
                        name: "Exit",
                        onClick: () => {
                            if(tutorialComponent?.isActive) return;
                            this._onClose();
                        }
                    }
                ]
            },
            {
                name: "Edit",
                subcontents: [
                    {
                        name: "Copy email",
                        onClick: () => {
                            navigator.clipboard.writeText(
                                removeTagsFromString(this.selectedMailContents.getContentsText().text)
                            );
                        }
                    },
                    {
                        name: "Delete email",
                        onClick: () => {
                            tutorialComponent?.onActivate?.();
                            if(
                                !this.selectedMailRef ||
                                MailCore.isEmailACase(this.selectedMailRef.id)
                            ) return;
                            if(GAME_DEBUG_MODE) {
                                console.log("Deleting selected email of id %s", this.selectedMailRef.id);
                            }
                            MailCore.deleteMailInstance(this.selectedMailRef.id);
                            PlayerDataSingleton.dirtify(SAVE_KEYS.MailboxContents);
                            this.emailsPool.forEach((e) => e.setSelected(false));
                            this.selectedMailRef = null;
                            selectedMailContainer.update(null);
                        }
                    }
                ]
            },
            {
                name: "Sort",
                onClick: () => {
                    if(GAME_DEBUG_MODE) {
                        console.log("Sorting inbox");
                    }
                    MailCore["mailContentsRef"] = MailCore["mailContentsRef"].sort((a, b) => {
                        if(a.type === MailType.CASE && b.type === MailType.CASE) return 0;
                        else if(a.type === MailType.CASE) return -1;
                        else return 1;
                    });
                    PlayerDataSingleton.dirtify(SAVE_KEYS.MailboxContents);
                    this.forceUpdate();
                }
            },
            {
                name: "Clean",
                onClick: () => {
                    if(GAME_DEBUG_MODE) {
                        console.log("Cleaning inbox");
                    }
                    const oldLen = this.mailDatasRef.length;
                    MailCore.deleteAllCaseUpdates();
                    PlayerDataSingleton.dirtify(SAVE_KEYS.MailboxContents);
                    const newLen = this.mailDatasRef.length;
                    if(newLen < oldLen) {
                        (ENGINE.getActiveState() as Windows95)["_taskbar"]?.showNotification({
                            id: "CLEAN_MAIL_" + uid(),
                            iconKey: "mail-icon",
                            titleStr: "Inbox cleaned!",
                            mainStr: `Your inbox has been successfully cleaned!`
                        });
                    }
                }
            },
        ]);
        fileEditViewText.addComponent(tutorialComponent);
        fileEditViewText.position.set(6 + this.contentContainer.position.x, 5 + this.contentContainer.position.y);
        this.addChild(fileEditViewText);
        // const fileEditViewText = new Text("File   Edit   Mail   Help", new TextStyle({
        //     fontFamily: GAME_FONT,
        //     fill: 0x000000,
        //     fontSize: 22,
        // }));
        // fileEditViewText.scale.set(0.5);
        // fileEditViewText.anchor.set(0, 0);
        // fileEditViewText.position.set(6, 5);
        // this.contentContainer.addChild(fileEditViewText);
    }

    private set mailDatasRef(_mailDatasRef: IMailInstanceData[]) {
        this.prevLength = this._mailDatasRef.length;
        this._mailDatasRef = _mailDatasRef;
    }

    private get mailDatasRef(): IMailInstanceData[] {
        return this._mailDatasRef;
    }

    onStep(_dt: number): void {
        super.onStep(_dt);
        if(this.prevLength !== this._mailDatasRef.length) {
            this.forceUpdate();
        }
    }

    public forceUpdate(): void {
        const delta = this._mailDatasRef.length - this.prevLength;
        this.prevLength = this._mailDatasRef.length;
        this.emailsPool.forEach((e, index) => {
            e.update(this.mailDatasRef[index+this.mailsOffset]);
        });
        this.patientPortraitStuff.container.visible = false;
        if(this.selectedMailRef) {
            const newMailData
                = this._mailDatasRef.find((e) => e.id === this.selectedMailRef.id);
            if(!newMailData) {
                // clear selected thing
                this.selectedMailRef = null;
                this.emailsPool.forEach((e) => e.setSelected(false));
            } else {
                this.selectedMailRef = newMailData;
            }
            this.selectedMailContents.update(this.selectedMailRef);
        }

        this.scrollBar.update(
            Math.max(this._mailDatasRef.length - 10, 0)
        );
    }

    private static createSelectedMailSection(
        _instance: Win95MailWindow,
        _bgHeight: number,
        _onHyperlinkClicked: (_partialUrl: string) => void,
    ): {
        container: GameObject,
        contentContainer: Container,
        selectedMailBg: NineSlicePlane,
        // contentsText: TaggedText,
        getContentsText: () => TaggedText,
        subjectContentsText: Text,
        update: (_email: IMailInstanceData) => void;
        onResize: (_dimensions: IVector2) => void;
        portraitContainer: Container;
        portraitSprite: Sprite;
    } {
        const selectedMailContainer = new GameObject();
        selectedMailContainer.addComponent(new TutorialStageComponent({
            id: "CaseEmailStage"
        }));
        const selectedMailBg = new NineSlicePlane(
            ENGINE.getPIXIResource("inner-container-nineslice") as Texture
        );
        selectedMailBg.width = tsthreeConfig.width;
        selectedMailBg.height = _bgHeight;
        selectedMailContainer.addChild(selectedMailBg);

        const contentsContainer = new Container();
        contentsContainer.position.set(0, 40);
        selectedMailContainer.addChild(contentsContainer);
        // this.addChild(selectedMailContainer);

        const fromToSubjectHeaderBg = new Graphics();
        fromToSubjectHeaderBg.beginFill(0xc0c0c0, 1);
        fromToSubjectHeaderBg.drawRect(0, 0, tsthreeConfig.width - 4, 40);
        fromToSubjectHeaderBg.endFill();
        fromToSubjectHeaderBg.position.set(2, 2);
        selectedMailContainer.addChild(fromToSubjectHeaderBg);

        const mask = new Graphics();
        mask.beginFill(0xff0000, 1);
        mask.drawRect(0, 0,
            selectedMailBg.width - 4,
            selectedMailBg.height - 40 - 4
        );
        mask.endFill();
        mask.position.set(2, 2);
        contentsContainer.mask = mask;
        contentsContainer.addChild(mask);

        // portrait shit
        const portraitContainer = new Container();
        contentsContainer.addChild(portraitContainer);
        portraitContainer.visible = false;
        const portraitContainerMask = new Graphics();
        portraitContainerMask.beginFill(0xff0000, 1);
        portraitContainerMask.drawRect(0, 0, 128, 126);
        portraitContainerMask.endFill();
        portraitContainer.addChild(portraitContainerMask);
        portraitContainerMask.position.set(20, 20);
        const portraitBg = new NineSlicePlane(
            ENGINE.getTexture("inner-container-nineslice")
        );
        portraitBg.width =
            portraitBg.height = 128;
        portraitBg.position.set(
            20, 20
        );
        portraitContainer.addChild(portraitBg);
        const portraitSprite = new Sprite();
        portraitSprite.mask = portraitContainerMask;
        portraitSprite.scale.set(1.36);
        portraitSprite.position.set(-1, -7);
        portraitContainer.addChild(portraitSprite);

        const boldTextStyle = new TextStyle({
            fontFamily: GAME_FONT_BOLD,
            fill: 0x000000,
            fontSize: 11,
            align: "left"
        });

        const htmlFontStyle: TextStyleSet = _instance.htmlTextStyle = {
            "default": {
                fontFamily: GAME_FONT,
                fill: 0x000000,
                fontSize: 11 * _instance.zoomFactor,
                align: "left",
                wordWrap: true,
                wordWrapWidth: (tsthreeConfig.width - 32)
            },
            "red": {
                fill: "#ff0000",
            },
            "green": {
                fontFamily: GAME_FONT_BOLD,
                // fontWeight: "bold",
                fill: "#00aa00",
            },
            "bold": {
                fontFamily: GAME_FONT_BOLD,
                // fontWeight: "bold"
            },
            "i": {
                fontStyle: "italic",
            },
            "hyperlink": {
                fill: "#0000ff",
                textDecoration: "underline"
            },
        };
        let contentsText: TaggedText | null = new TaggedText("",
            htmlFontStyle,
            {
                drawWhitespace: DRAW_WHITESPACE_IN_MAIN_TEXT,
                supressConsole: true,
            }
        );
        // contentsText.scale.set(0.5);
        (contentsText as unknown as Text).position.set(10, 10);
        contentsContainer.addChild(contentsText as unknown as Text);

        const mailContentsScrollbar = new ScrollBar({
            height: selectedMailBg.height - 40 - 8,
            onScroll: (_currIncrements: number) => {
                contentsText?.position.set(
                    contentsText.position.x,
                    (_currIncrements * -16) + 10
                );
                _instance.patientPortraitStuff.container.position.set(
                    _instance.patientPortraitStuff.container.position.x,
                    (_currIncrements * -16)
                );
            }
        });
        mailContentsScrollbar.position.set(tsthreeConfig.width - 2, 2);
        contentsContainer.addChild(mailContentsScrollbar);
        let pointerOver = false;
        contentsContainer.interactive = true;
        contentsContainer.buttonMode = false;
        contentsContainer.on("pointerover", () => {
            pointerOver = true;
        });
        contentsContainer.on("pointerout", () => {
            pointerOver = false;
        });
        document.addEventListener("wheel", (ev) => {
            if(
                !selectedMailContainer.parent?.visible ||
                !pointerOver
            ) return;
            mailContentsScrollbar.scrollWheel((ev.deltaY / 100));
        });

        const fromText = new Text("From:", boldTextStyle);
        fromText.position.set(10, 6);
        fromToSubjectHeaderBg.addChild(fromText);

        const fromContentsText = new Text(
            "",
            Object.assign({}, boldTextStyle, {
                fontFamily: GAME_FONT
            })
        );
        fromContentsText.position.set(65, fromText.y);
        fromToSubjectHeaderBg.addChild(fromContentsText);

        const subjectText = new Text("Subject:", boldTextStyle);
        subjectText.position.set(10, 22);
        fromToSubjectHeaderBg.addChild(subjectText);

        const subjectContentsText = new Text(
            "",
            Object.assign({}, boldTextStyle, {
                fontFamily: GAME_FONT
            })
        );
        subjectContentsText.position.set(65, subjectText.y);
        fromToSubjectHeaderBg.addChild(subjectContentsText);

        const toText: Text = new Text(
            "To:",
            boldTextStyle
        );
        toText.position.set(
            fromText.x + fromText.width + fromContentsText.x + fromContentsText.width,
            fromText.y
        );
        fromToSubjectHeaderBg.addChild(toText);

        const toContentsText: Text = new Text(
            "",
            Object.assign({}, boldTextStyle, {fontFamily: GAME_FONT})
        );
        toContentsText.position.set(
            toText.x + toText.width + 6,
            toText.y
        );
        fromToSubjectHeaderBg.addChild(toContentsText);

        const zoom = (_upOrDown: "up" | "down") => {
            Win95MailWindow._mailTextCache = {};
            if(_upOrDown === "up") {
                _instance.zoomFactor = Math.round(Math.min(
                    _instance.zoomFactor + 0.2,
                    3
                ) * 100) / 100;
            } else if(_upOrDown === "down") {
                _instance.zoomFactor = Math.round(Math.max(
                    _instance.zoomFactor - 0.2,
                    0.6
                ) * 100) / 100;
            }
            if(contentsText) {
                contentsText.cacheAsBitmap = false;
                htmlFontStyle.default.fontSize
                    = contentsText.defaultStyle.fontSize
                        = 11 * _instance.zoomFactor;
                contentsText.update(false);
            } else {
                htmlFontStyle.default.fontSize
                    = 11 * _instance.zoomFactor;
            }
            zoomFactor.text = _instance.zoomFactor.toString();
            PlayerDataSingleton.setMailboxZoom(_instance.zoomFactor);
            update(_instance.selectedMailRef);
        };
        const zoomTextStyle = new TextStyle({
            align: "center",
            fontFamily: GAME_FONT_BOLD,
            fontSize: 18,
        });
        const zoomContainer = new Container();
        const zoomUpButton = new Text("+", zoomTextStyle);
        buttonify(zoomUpButton, {
            onFire: () => zoom("up"),
        });
        zoomUpButton.anchor.set(0.5);
        zoomUpButton.position.set(16, 0);
        zoomContainer.addChild(zoomUpButton);
        const zoomDownButton = new Text("-", zoomTextStyle);
        buttonify(zoomDownButton, {
            onFire: () => zoom("down"),
        });
        zoomDownButton.anchor.set(0.5);
        zoomDownButton.position.set(-16, 0);
        zoomContainer.addChild(zoomDownButton);
        const zoomFactor = new Text(_instance.zoomFactor.toString(),
            Object.assign({}, zoomTextStyle, {fontSize: 12})
        );
        zoomFactor.anchor.set(0.5, 0.5);
        zoomContainer.addChild(zoomFactor);
        zoomContainer.position.set(
            selectedMailBg.width - 30,
            selectedMailBg.height + 9
        );
        selectedMailContainer.addChild(zoomContainer);

        const update = async (_email?: IMailInstanceData): Promise<void> => {
            // if(MailCore.isEmailACaseUpdate(_email)) {
            //     toContentsText.text = "You";
            //     subjectContentsText.text = ;
            //     fromContentsText.text = data.from;
            //     contentsText.text = data.contents;
            // } else {
            contentsText?.position.set(10, 10);
            _instance.patientPortraitStuff.container.position.set(0, 0);
            if(_email) {
                const data = MailCore.getDataFromMailInstance(_email);
                _instance.patientPortraitStuff.container.visible = false;
                _instance.patientPortraitStuff.container.scale.set(_instance.zoomFactor);
                toContentsText.text = data.to;
                subjectContentsText.text = data.subject;
                fromContentsText.text = data.from;
                _instance.popoutText.visible = true;
                _instance.popoutSprite.visible = true;
                if(contentsText) {
                    contentsText?.parent?.removeChild(contentsText);
                }
                // contentsText.text = data.contents;
                if(
                    !MailCore.isEmailACase(_email.id, _email.type) &&
                    Win95MailWindow._mailTextCache[_email.id]
                ) {
                    contentsText = Win95MailWindow._mailTextCache[_email.id];
                    contentsText.text = data.contents;
                    contentsContainer.addChild(contentsText);
                    // } else if(MailCore.isEmailACase(_email.id, _email.type)) {
                    //     contentsText.cacheAsBitmap = false;
                    //     contentsText.text = data.contents;
                    //     (contentsText as unknown as Sprite).position.set(10, 10);
                    //     HelperFunctions.makeInteractive(contentsText, true);
                    //     contentsText.update();
                    //     parentCache.addChild(contentsText);
                } else {
                    if(MailCore.isEmailACase(_email.id, _email.type)) {
                        // update patient pic singleton
                        // CharacterPortraitGenerator()
                        const patientName =
                            PlayerDataSingleton.getCases().find((e) => {
                                return e.id === _email.id.split("_")[1];
                            })?.patientName;
                        if(_instance.patientPortraitStuff.sprite.texture) {
                            _instance.patientPortraitStuff.sprite.texture = null;
                        }
                        if(_instance.patientPortraitStuff.currentTexture) {
                            _instance.patientPortraitStuff.currentTexture.destroy(true);
                        }

                        if(patientName) {
                            const texture = createPatientPictureFromPatientName(patientName);
                            _instance.patientPortraitStuff.currentTexture = texture;
                            _instance.patientPortraitStuff.sprite.texture = texture;
                        }
                        _instance.patientPortraitStuff.container.visible = true;
                    }

                    contentsText = new TaggedText(data.contents,
                        htmlFontStyle,
                        {
                            drawWhitespace: DRAW_WHITESPACE_IN_MAIN_TEXT,
                            supressConsole: true,
                        }
                    );
                    contentsText.cacheAsBitmap = data.contents.indexOf("<hyperlink") === -1;
                    (contentsText as unknown as Sprite).position.set(10, 10);
                    HelperFunctions.makeInteractive(contentsText, true);
                    HelperFunctions.makeInteractive(contentsContainer, true);
                    contentsText.update();
                    contentsContainer.addChild(contentsText);
                    Win95MailWindow._mailTextCache[_email.id] = contentsText;
                }
            } else {
                toContentsText.text = "";
                subjectContentsText.text = "";
                fromContentsText.text = "";
                _instance.popoutText.visible = false;
                _instance.popoutSprite.visible = false;
                if(contentsText) {
                    contentsText.text = "";
                    contentsText.parent.removeChild(contentsText);
                    contentsText = null;
                }
            }
            // }
            toText.position.set(
                fromText.x + fromText.width + fromContentsText.x + fromContentsText.width,
                fromText.y
            );
            toContentsText.position.set(
                toText.x + toText.width + 6,
                toText.y
            );
            if(contentsText) {
                HelperFunctions.makeInteractive(contentsText, true);
                contentsText?.update();
                this.updateHyperlinksInText(contentsText, _onHyperlinkClicked);
            }

            const newHeightIncrements = Math.ceil((contentsContainer.height - (selectedMailBg.height - 40 - 6)) / 16);
            mailContentsScrollbar.update(
                newHeightIncrements
            );
        };

        return {
            container: selectedMailContainer,
            contentContainer: contentsContainer,
            selectedMailBg: selectedMailBg,
            getContentsText: () => contentsText,
            subjectContentsText: subjectContentsText,
            update: update,
            onResize: (_dimensions) => {

            },
            portraitContainer: portraitContainer,
            portraitSprite: portraitSprite,
        };
    }

    public static updateHyperlinksInText(_target: TaggedText, _onHyperlinkClicked: (_s: string) => void): void {
        _target.textFields.forEach((e) => {
            // @ts-ignore
            const targetUrl: string = e.style?.url;
            if(targetUrl) {
                buttonify(e, {
                    onFire: () => {
                        // @ts-ignore
                        const url = e.style?.url;
                        // ^this is needed because TaggedText removes = from style texts for some reason

                        if(url) {
                            _onHyperlinkClicked(url);
                        }
                    },
                    onPointerOver: () => {},
                    onPointerOut: () => {}
                });
            }
        });
    }

    private static createTab(title: string, width?: number): Container {
        const container = new Container();
        const tab = new NineSlicePlane(
            ENGINE.getPIXIResource("tab-nineslice") as Texture,
        );
        if(width) {
            tab.width = width;
        }
        // tab.position.set(
        //     2, 2
        // );
        const subjectTabText = new Text(title, new TextStyle({
            fontFamily: GAME_FONT,
            fill: 0x000000,
            fontSize: 22,
            align: "left"
        }));
        subjectTabText.scale.set(0.5);
        subjectTabText.position.set(8, 1);
        tab.addChild(subjectTabText);
        container.addChild(tab);
        return container;
    }

    private static createMailLine(settings: {
        tabWidths: number[];
        // mailData: IMailData
    }): {
        container: GameObject,
        update: (_case: IMailInstanceData) => void,
        setSelected: (_selected: boolean) => void,
    } {
        const container = new GameObject();
        container.addComponent(
            new TutorialStageComponent({
                id: "MailLineStage",
            })
        );

        const textStyle = new TextStyle({
            fontFamily: GAME_FONT,
            fontSize: 11,
            fill: 0x000000,
            align: "left"
        });
        const yOffset = 1;

        const selectedBg = new Graphics();
        selectedBg.beginFill(0x0000ff);
        selectedBg.drawRect(2, 0, settings.tabWidths.reduce(
            (p, c, i, a) => p + c
        ) - 17, 16);
        selectedBg.endFill();
        selectedBg.visible = false;
        container.addChild(selectedBg);

        const icon = new Sprite(ENGINE.getPIXIResource("unread-icon") as Texture);
        icon.width = icon.height = 16;
        icon.anchor.set(0, 0);
        icon.position.set(12, 0);
        container.addChild(icon);

        const fromText = new Text("", textStyle);
        fromText.anchor.set(0, 0);
        // fromText.scale.set(0.5);
        fromText.position.set(
            16 + icon.width,
            yOffset,
        );
        container.addChild(fromText);
        const fromTextMask = new Graphics();
        fromTextMask.beginFill(0xff0000);
        fromTextMask.drawRect(0, 0, settings.tabWidths[0], 16);
        fromTextMask.endFill();
        fromTextMask.position.set(
            -fromText.x,
            -fromText.y,
        );
        fromText.mask = fromTextMask;
        fromText.addChild(fromTextMask);

        const subjectText = new Text("", textStyle);
        subjectText.anchor.set(0, 0);
        // subjectText.scale.set(0.5);
        subjectText.position.set(
            settings.tabWidths[0] + 6,
            yOffset,
        );
        container.addChild(subjectText);
        const subjectTextMask = new Graphics();
        subjectTextMask.beginFill(0xff0000);
        subjectTextMask.drawRect(0, 0, settings.tabWidths[1], 16);
        subjectTextMask.endFill();
        subjectTextMask.position.set(
            -4,
            0,
        );
        subjectText.mask = subjectTextMask;
        subjectText.addChild(subjectTextMask);

        const receivedText = new Text("", textStyle);
        receivedText.anchor.set(0, 0);
        // receivedText.scale.set(0.5);
        receivedText.position.set(
            settings.tabWidths[0] + settings.tabWidths[1] + 6,
            yOffset,
        );
        container.addChild(receivedText);
        const receivedTextMask = new Graphics();
        receivedTextMask.beginFill(0xff0000);
        receivedTextMask.drawRect(0, 0, settings.tabWidths[2] - 17, 16);
        receivedTextMask.endFill();
        receivedTextMask.position.set(
            -4,
            0,
        );
        receivedText.mask = receivedTextMask;
        receivedText.addChild(receivedTextMask);

        return {
            setSelected: (_selected: boolean) => {
                if(_selected) {
                    container.getComponent(TutorialStageComponent)?.onActivate?.();
                    selectedBg.visible = true;
                    textStyle.fill = "white";
                } else {
                    selectedBg.visible = false;
                    textStyle.fill = "black";
                }
            },
            update: (_mail: IMailInstanceData) => {
                if(!_mail) {
                    icon.visible = false;
                    receivedText.text = "";
                    subjectText.text = "";
                    fromText.text = "";
                    return;
                } else {
                    icon.visible = true;
                }

                switch(_mail.state) {
                    case MailState.READ:
                        if(_mail.type == MailType.CASE) {
                            icon.texture = ENGINE.getPIXIResource("read-case-icon") as Texture;
                        } else {
                            icon.texture = ENGINE.getPIXIResource("read-icon") as Texture;
                        }
                        break;
                    case MailState.UNREAD:
                        if(_mail.type == MailType.CASE) {
                            icon.texture = ENGINE.getPIXIResource("unread-case-icon") as Texture;
                        } else {
                            icon.texture = ENGINE.getPIXIResource("unread-icon") as Texture;
                        }
                        break;
                }

                // if(_mail.type == MailType.CASE_UPDATE) {
                //     // `CASE_UPDATE-${_patientName}-${SpecialPageTypes.Exams}-${_id}`,
                //     const splitStr = _mail.id.split("-");
                //     const _patientName = splitStr[1];
                //     const _type: SpecialPageTypes = splitStr[2] as SpecialPageTypes;
                //     let _typeStr = "";
                //     switch(_type) {
                //         case SpecialPageTypes.Exams:
                //             _typeStr = "Examination";
                //             break;
                //         case SpecialPageTypes.Treatments:
                //             _typeStr = "Treatment";
                //             break;
                //         case SpecialPageTypes.Diagnoses:
                //             _typeStr = "Diagnosis";
                //             break;
                //     }
                //
                //     const _id = splitStr[3];
                //     receivedText.text = formatTimestamp(_mail.dateReceived);
                //     subjectText.text = `${_typeStr} "${HospitalIDMapper.idToStringObject[_id]}" successfully requested`;
                //     fromText.text = "DoctOS Case Server";
                // } else {
                    const data = MailCore.getDataFromMailInstance(_mail);
                    receivedText.text =
                        PlayerDataSingleton.getUse24HrClock() ?
                            formatTimestampToHHMM(_mail.dateReceived, true)
                            :
                            formatTimestampAs12HrClock(
                                _mail.dateReceived,
                                true
                            );

                    subjectText.text = data.subject;
                    fromText.text = data.from;
                // }
            },
            container: container,
        };
    }

    protected resize(_newDimensions: IVector2): void {
        super.resize(_newDimensions);

    }
}